'use client'

import { useState, useRef, useEffect, useMemo } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Play, Square, RotateCcw } from 'lucide-react'
import { countWords } from '@/components/tools/word-counter'

// Neutral sample for measuring the reader's own words per minute.
const SAMPLE = `This paragraph gives you a consistent passage for measuring your speaking pace. Read it in the same voice and delivery style you would use while recording a video. Do not rush to reach a particular score. The useful result is your own words-per-minute value, which you can enter into a script-length calculator. Pace is only one production choice. Pauses, emphasis, demonstrations, visual sequences, and edits can all change the final runtime. If the first reading feels unnatural, reset the timer and try again. You can also paste a section from your actual script and time that passage instead. That approach may better reflect its vocabulary, sentence length, and delivery. Keep the measured number as a planning input, then allow additional time for anything that will not be spoken aloud.`

const SAMPLE_WORDS = countWords(SAMPLE)

function classify(wpm: number): { band: string; color: string; guidance: string } {
  if (wpm < 120) {
    return {
      band: 'Under 120 WPM',
      color: 'text-blue-400',
      guidance: 'Use this measured pace as a planning input; the tool does not infer how viewers will respond.',
    }
  }
  if (wpm < 150) {
    return {
      band: '120 to 149 WPM',
      color: 'text-green-500',
      guidance: 'Use this measured pace as a planning input; the tool does not infer how viewers will respond.',
    }
  }
  if (wpm < 180) {
    return {
      band: '150 to 179 WPM',
      color: 'text-yellow-500',
      guidance: 'Use this measured pace as a planning input; the tool does not infer how viewers will respond.',
    }
  }
  return {
    band: '180+ WPM',
    color: 'text-orange-500',
    guidance: 'Use this measured pace as a planning input; the tool does not infer how viewers will respond.',
  }
}

type Mode = 'read' | 'paste'

interface TalkingSpeedTestProps {
  /**
   * The text pasted in the paste-and-time mode. The page's own client wrapper
   * owns it so the free check below the tool can run on the same script; the
   * read-aloud mode measures its own fixed passage and ignores it.
   */
  script: string
  onScriptChange: (value: string) => void
}

export function TalkingSpeedTest({ script, onScriptChange }: TalkingSpeedTestProps) {
  const [mode, setMode] = useState<Mode>('read')

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-2 rounded-lg border border-border bg-card p-1">
        <button
          onClick={() => setMode('read')}
          className={`rounded-md px-3 py-2 text-sm font-medium transition-colors ${
            mode === 'read' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          Read-aloud test
        </button>
        <button
          onClick={() => setMode('paste')}
          className={`rounded-md px-3 py-2 text-sm font-medium transition-colors ${
            mode === 'paste' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          Paste &amp; time
        </button>
      </div>

      {mode === 'read' ? <ReadAloudMode /> : <PasteMode text={script} onTextChange={onScriptChange} />}
    </div>
  )
}

function ReadAloudMode() {
  const [running, setRunning] = useState(false)
  const [elapsedMs, setElapsedMs] = useState(0)
  const [finalWpm, setFinalWpm] = useState<number | null>(null)
  const startRef = useRef<number>(0)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [])

  const start = () => {
    setFinalWpm(null)
    setElapsedMs(0)
    startRef.current = Date.now()
    setRunning(true)
    intervalRef.current = setInterval(() => {
      setElapsedMs(Date.now() - startRef.current)
    }, 100)
  }

  const stop = () => {
    if (intervalRef.current) clearInterval(intervalRef.current)
    const seconds = (Date.now() - startRef.current) / 1000
    setRunning(false)
    if (seconds > 1) {
      setFinalWpm(Math.round((SAMPLE_WORDS / seconds) * 60))
    }
  }

  const reset = () => {
    if (intervalRef.current) clearInterval(intervalRef.current)
    setRunning(false)
    setElapsedMs(0)
    setFinalWpm(null)
  }

  const seconds = Math.floor(elapsedMs / 1000)
  const cls = finalWpm !== null ? classify(finalWpm) : null

  return (
    <div className="space-y-5">
      <Card>
        <CardContent className="p-5">
          <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">
            Read this aloud at your normal pace ({SAMPLE_WORDS} words)
          </p>
          <p className="text-sm leading-relaxed text-foreground">{SAMPLE}</p>
        </CardContent>
      </Card>

      <div className="text-center">
        <div className="text-5xl font-bold text-foreground tabular-nums" aria-live="polite">
          {Math.floor(seconds / 60)}:{(seconds % 60).toString().padStart(2, '0')}
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          {running ? 'Recording. Press Stop when you finish reading.' : 'Press Start, read the paragraph aloud, then Stop.'}
        </p>
      </div>

      <div className="flex justify-center gap-3">
        {!running ? (
          <button onClick={start} className="inline-flex items-center gap-2 rounded-md bg-primary px-6 py-3 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors">
            <Play className="h-4 w-4" /> Start
          </button>
        ) : (
          <button onClick={stop} className="inline-flex items-center gap-2 rounded-md bg-primary px-6 py-3 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors">
            <Square className="h-4 w-4" /> Stop
          </button>
        )}
        <button onClick={reset} className="inline-flex items-center gap-2 rounded-md border border-border px-4 py-3 text-sm font-medium text-foreground hover:bg-secondary transition-colors">
          <RotateCcw className="h-4 w-4" /> Reset
        </button>
      </div>

      {finalWpm !== null && cls && <ResultCard wpm={finalWpm} cls={cls} />}
    </div>
  )
}

function PasteMode({ text, onTextChange }: { text: string; onTextChange: (value: string) => void }) {
  const [mins, setMins] = useState('')
  const [secs, setSecs] = useState('')

  const { wpm, words } = useMemo(() => {
    const w = countWords(text)
    const totalSeconds = (parseInt(mins) || 0) * 60 + (parseInt(secs) || 0)
    if (w === 0 || totalSeconds === 0) return { wpm: null as number | null, words: w }
    return { wpm: Math.round((w / totalSeconds) * 60), words: w }
  }, [text, mins, secs])

  const cls = wpm !== null ? classify(wpm) : null

  return (
    <div className="space-y-5">
      <div>
        <label className="mb-2 block text-sm font-medium text-foreground">Paste what you said (or your script)</label>
        <textarea
          value={text}
          onChange={(e) => onTextChange(e.target.value)}
          placeholder="Paste the text you read aloud..."
          className="h-36 w-full resize-y rounded-md border border-border bg-background px-4 py-3 text-sm leading-relaxed text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/50"
        />
        <p className="mt-1 text-xs text-muted-foreground">{words.toLocaleString()} words</p>
      </div>

      <div>
        <label className="mb-2 block text-sm font-medium text-foreground">How long did it take?</label>
        <div className="flex items-center gap-2">
          <input type="number" inputMode="numeric" min={0} value={mins} onChange={(e) => setMins(e.target.value)} placeholder="min" className="w-24 rounded-md border border-border bg-background px-3 py-2 text-center text-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/50" />
          <span className="text-muted-foreground">min</span>
          <input type="number" inputMode="numeric" min={0} max={59} value={secs} onChange={(e) => setSecs(e.target.value)} placeholder="sec" className="w-24 rounded-md border border-border bg-background px-3 py-2 text-center text-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/50" />
          <span className="text-muted-foreground">sec</span>
        </div>
      </div>

      {wpm !== null && cls ? (
        <ResultCard wpm={wpm} cls={cls} />
      ) : (
        <Card>
          <CardContent className="p-5 text-center text-sm text-muted-foreground">
            Enter your text and the time it took to see your words per minute.
          </CardContent>
        </Card>
      )}
    </div>
  )
}

function ResultCard({ wpm, cls }: { wpm: number; cls: { band: string; color: string; guidance: string } }) {
  return (
    <Card className="border-primary/30">
      <CardContent className="p-6 text-center">
        <div className="text-xs uppercase tracking-wide text-muted-foreground">Your speaking pace</div>
        <div className="mt-1 text-5xl font-bold text-primary tabular-nums">{wpm}</div>
        <div className="text-xs text-muted-foreground">words per minute</div>
        <div className={`mt-3 text-sm font-semibold ${cls.color}`}>{cls.band}</div>
        <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">{cls.guidance}</p>
      </CardContent>
    </Card>
  )
}
